import React, { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';

import DisplayDescription from 'client/app/apps/protocols/annotations/DisplayDescription';
import { useProtocolInstanceContext } from 'client/app/apps/protocols/context/ProtocolInstanceProvider';
import { useSimulationContext } from 'client/app/apps/protocols/context/SimulationProvider';
import { useStepsContext } from 'client/app/apps/protocols/context/StepsProvider/StepsProvider';
import { useWorkflowContext } from 'client/app/apps/protocols/context/WorkflowProvider';
import { InputStepList } from 'client/app/apps/protocols/InputStepList';
import {
  InputStepName,
  InputStepParameters,
  ParameterEditorList,
  ParameterSkeletonList,
} from 'client/app/apps/protocols/InputStepParameters';
import {
  OutputStepPreviewPanel,
  OutputStepPreviewTitle,
} from 'client/app/apps/protocols/OutputStepPreview';
import { OutputStepResponse } from 'client/app/apps/protocols/OutputStepResponse/OutputStepResponse';
import { OutputStepResponseValidationIndicator } from 'client/app/apps/protocols/OutputStepResponse/OutputStepResponseValidationIndicator';
import { useStepsResponse } from 'client/app/apps/protocols/OutputStepResponse/useStepsReponse';
import { ProtocolHeader } from 'client/app/apps/protocols/ProtocolHeader';
import ProtocolInfo from 'client/app/apps/protocols/ProtocolInfo';
import { ProtocolInstanceReadOnlyBar } from 'client/app/apps/protocols/ProtocolInstanceReadOnlyBar';
import { SimulateProtocolInstanceButton } from 'client/app/apps/protocols/SimulateProtocolInstanceButton';
import { ViewProtocolInstanceSimulationButton } from 'client/app/apps/protocols/ViewProtocolInstanceSimulationButton';
import { WarningBar } from 'client/app/apps/protocols/WarningBar';
import { useCurrentEntity } from 'client/app/components/nav/breadcrumbs/BreadcrumbsEntityContext';
import Colors from 'common/ui/Colors';
import { TemplateWorkflowIcon } from 'common/ui/icons/TemplateWorkflow';
import { useAdditionalPanelContext } from 'common/ui/providers/AdditionalPanelProvider';

export const EditProtocolInstance = () => {
  const {
    loading: workflowLoading,
    elementsConfig,
    elementsContext,
    updateOutput,
  } = useWorkflowContext();
  const {
    loading: protocolInstanceLoading,
    protocolInstance,
    getProtocolStepParameterInfo,
    updateProtocolInput,
    refetchingReason,
  } = useProtocolInstanceContext();
  const { additionalPanelContents } = useAdditionalPanelContext();
  const { simulation, isSimulating, clearSimulation } = useSimulationContext();
  const { workflowSchema, stepsConfig, selectedStep, handleSelectStep } =
    useStepsContext();
  const stepsResponse = useStepsResponse(
    stepsConfig,
    elementsConfig,
    elementsContext,
    simulation?.errors,
  );
  const [hideErrors, setHideErrors] = useState(false);
  const activeOutput = selectedStep?.outputs[0];

  const { showInfo } = useCurrentEntity();
  // Because the `showInfo` state is lifted, it isn't reset when the component changes.
  // So we store and compare against the initial value to ensure we default to not showing
  const [initialShowInfo] = useState(showInfo);

  const stepName = selectedStep
    ? selectedStep.displayName
    : workflowLoading
    ? 'Loading step...'
    : 'Unnamed step';

  const outputTitle = activeOutput
    ? `Preview ${activeOutput.displayName}`
    : workflowLoading
    ? 'Loading preview...'
    : 'Preview';

  // awkwardly element output preview depends on workflow builder context
  useEffect(() => {
    activeOutput && updateOutput(activeOutput.id);
  }, [activeOutput, updateOutput]);

  const isOutputLoading = workflowLoading || protocolInstanceLoading;
  const isExecuted = protocolInstance.execution?.id !== undefined;
  const isNotOwnedByUser = !protocolInstance.isOwnedByUser;
  const isReadOnly = isExecuted || isNotOwnedByUser;
  const isParameterListLoading =
    workflowLoading || refetchingReason !== undefined || selectedStep === undefined;

  return (
    <>
      {isReadOnly && (
        <ProtocolInstanceReadOnlyBar
          protocolInstanceId={protocolInstance.id}
          author={isNotOwnedByUser ? protocolInstance.createdBy.displayName : undefined}
        />
      )}
      {refetchingReason && <WarningBar message={refetchingReason} />}
      <ProtocolHeader
        icon={<StyledTemplateWorkflowIcon />}
        title={protocolInstance.name}
        description="Modify the inputs of your protocol"
      >
        {isReadOnly ? (
          <ViewProtocolInstanceSimulationButton
            simulationId={protocolInstance.simulation?.id}
            tooltip={
              isExecuted ? 'View the protocol simulation that has been executed' : ''
            }
          />
        ) : (
          <SimulateProtocolInstanceButton
            loading={isOutputLoading}
            schema={workflowSchema}
            elementsContext={elementsContext}
          />
        )}
      </ProtocolHeader>
      <GridContainer>
        <InputStepList />
        <SelectedStepPanel key={selectedStep?.id}>
          <InputStepParameters
            header={<InputStepName variant="h4">{stepName}</InputStepName>}
          >
            {/* don't place description in header. It could be very long in which case we want it to scroll with the inputs */}
            <Box marginBottom={5}>
              <DisplayDescription description={selectedStep?.displayDescription} />
            </Box>
            {isParameterListLoading ? (
              <ParameterSkeletonList />
            ) : (
              <ParameterEditorList
                inputParameters={selectedStep.inputs.map(getProtocolStepParameterInfo)}
                updateParameter={updateProtocolInput}
                isDisabled={isReadOnly}
              />
            )}
          </InputStepParameters>
          <OutputStepPreviewPanel noOutput={activeOutput === undefined}>
            <Stack direction="row" justifyContent="space-between">
              <OutputStepPreviewTitle variant="h4">{outputTitle}</OutputStepPreviewTitle>
              <OutputStepResponseValidationIndicator
                // we should show a loading skeleton for output loading; so not busy
                loadingStatus={isOutputLoading ? 'neutral' : isSimulating ? 'busy' : 'ok'}
                stepsResponse={stepsResponse}
                buttonPrefix={hideErrors ? 'View' : 'Hide'}
                onClick={() => setHideErrors(prev => !prev)}
              />
            </Stack>
            <DisplayDescription description={activeOutput?.displayDescription} />
            <OutputStepResponse
              loading={isOutputLoading}
              selectedStepId={selectedStep?.id}
              ignoreErrors={hideErrors}
              stepsResponse={stepsResponse}
              handleSelectStep={handleSelectStep}
              clearSimulation={clearSimulation}
            />
          </OutputStepPreviewPanel>
        </SelectedStepPanel>
        {additionalPanelContents && (
          <SelectedStepPanelOverlay>{additionalPanelContents}</SelectedStepPanelOverlay>
        )}
        {showInfo !== initialShowInfo && (
          <ProtocolInfo protocol={protocolInstance.protocol} />
        )}
      </GridContainer>
    </>
  );
};

const GridContainer = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplate: `
      "list inputs outputs" minmax(400px, 1200px)
      / auto 1fr 1fr`,
  padding: theme.spacing(8),
  gap: theme.spacing(7),
  height: '100%',
  overflow: 'auto',
  backgroundColor: Colors.GREY_10,
  position: 'relative',
}));

const SelectedStepPanelOverlay = styled('div')({
  gridColumnStart: 'inputs',
  gridColumnEnd: 'outputs',
  gridRow: 1,
  display: 'flex',
  zIndex: 2,
});

const SelectedStepPanel = styled('div')({
  gridColumnStart: 'inputs',
  gridColumnEnd: 'outputs',
  gridRow: 1,
  display: 'flex',
  overflowX: 'auto',
  zIndex: 1,
});

const StyledTemplateWorkflowIcon = styled(TemplateWorkflowIcon)(() => ({
  color: '#1773C9',
}));
